import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import Flex from "../../components/structure/Flex";
import { Booking, CouponResponse } from "../../definitions/booking";
import { StringObject } from "../../definitions/general";
import { Property } from "../../definitions/property";
import { useAnonymousApi, useUnauthenticatedApi } from "../../hooks/useApi";
import useBookingService from "../../hooks/useBookingService";
import useWebview from "../../hooks/useWebview";
import { getTrackingInfo } from "../../utils/track";
import { Star } from "../assets/icons";
import RoomSummary from "./components/RoomSummary";
import Section from "./components/Section";

interface BookingWidgetProps {}

const GENDER: StringObject = {
  0: "M",
  1: "F",
  2: "O",
};

const BookingWidget: React.FC<BookingWidgetProps> = () => {
  const location = useLocation();
  const bookingService = useBookingService();
  const api = useUnauthenticatedApi("STAY_OPERATORS");
  const couponApi = useAnonymousApi("STAY_BOOKINGS_APPLY_COUPON");
  const { sendWebViewMessage, message } = useWebview();

  const [bookingDetails, setBookingDetails] = useState<Booking | undefined>();
  const [propertyInfo, setPropertyInfo] = useState<Property | undefined>();
  const [guestsDetails, setGuestDetails] = useState<any[]>([]);
  const [maxGuests, setMaxGuests] = useState<number>(1);
  const [couponResponse, setCouponResponse] = useState<
    CouponResponse | undefined
  >();

  useEffect(() => {
    const bookingDetails: Booking | null =
      bookingService.decodeBookingDetailsFromURI(location.search);
    if (bookingDetails) {
      console.log("Booking Details", bookingDetails);
      setBookingDetails({ ...bookingDetails, track: getTrackingInfo() });
      api
        .get(`/${bookingDetails.property_code}/`)
        .then((res) => {
          console.log("Operator", res);
          const _maxGuests = bookingService.getOccupancyFromBooking(
            bookingDetails,
            res.data.operator.rooms
          );
          const operatorData = bookingService.buildAddonRooms(
            res.data.operator
          );
          setPropertyInfo(operatorData);
          console.log("Max guests", _maxGuests);
          setMaxGuests(_maxGuests);
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      // history.push("/404");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (
      bookingDetails &&
      bookingDetails.checkin &&
      bookingDetails.checkout &&
      couponApi
    ) {
      try {
        console.log("Coupon:", bookingDetails);
        couponApi
          .post("", JSON.stringify(bookingDetails))
          .then((res) => {
            if (res && res.data && res.data.booking) {
              console.log("Coupon:", res);
              const rooms = bookingService.getRoomDataFromBooking(
                res.data.booking.rooms
              );
              res.data.booking.rooms = rooms;
              setCouponResponse(res.data.booking);
              sendWebViewMessage({ type: "react-loaded" });
            }
          })
          .catch((e) => {
            console.log(e);
          });
      } catch (error) {
        console.log(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDetails, couponApi]);

  useEffect(() => {
    if (bookingDetails && couponResponse && propertyInfo) {
      sendWebViewMessage({
        type: "booking-data",
        bookingDetails,
        couponResponse,
        operator: propertyInfo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDetails, couponResponse, propertyInfo]);

  useEffect(() => {
    if (message) {
      if (message.type === "edit-guests") {
        setGuestDetails(message.data);
      }
    }
  }, [message]);

  const handleEditGuest = () => {
    sendWebViewMessage({ type: "edit-guests" });
  };
  const handleHouseRules = () => {
    sendWebViewMessage({ type: "house-rules" });
  };
  const handleCancellationPolicy = () => {
    sendWebViewMessage({ type: "cancellation-policy" });
  };
  const handleTnC = () => {
    sendWebViewMessage({ type: "t-and-c" });
  };

  return propertyInfo && bookingDetails ? (
    <section className="flex flex-col bg-theme-background w-full h-content text-theme-text font-primary">
      <Section title="Your Stay">
        <Flex items="center" className="mt-4">
          <img
            src={propertyInfo?.images[0].image}
            alt={propertyInfo?.name}
            className="w-36 h-28 bg-theme-surface rounded-lg"
          />
          <Flex col className="ml-4">
            <span className="font-medium text-lg">{propertyInfo.name}</span>
            <Flex>
              <Star />
              <span className="text-theme-disabled text-sm ml-1">
                4.2 (23 reviews)
              </span>
            </Flex>
          </Flex>
        </Flex>
        <Flex col className="mt-6">
          <Flex items="center" justify="between">
            <span>Trip dates</span>
          </Flex>
          <span className="text-theme-placeholder text-lg mt-1">
            {moment(bookingDetails.checkin).format("ddd, D MMM")} -{" "}
            {moment(bookingDetails.checkout).format("ddd, D MMM")}
          </span>
        </Flex>
        <Flex col className="mt-6">
          <Flex items="center" justify="between">
            <span>Rooms</span>
          </Flex>
          {couponResponse &&
            bookingDetails.rooms.map((room) => {
              let ref: string = room._ref.split("*")[0];
              if (ref.endsWith("-")) {
                ref = ref.substr(0, ref.length - 1);
              }
              return (
                <RoomSummary
                  key={room._ref}
                  count={room.count}
                  meta={{
                    ...propertyInfo.rooms.find((_room) => _room._ref === ref),
                    ...couponResponse.rooms.find(
                      (_room: { _ref: string }) => _room._ref === ref
                    ),
                  }}
                />
              );
            })}
        </Flex>
        <Flex col className="mt-6">
          <Flex items="center" justify="between">
            <span>Guests</span>
            <button
              className="focus:outline-none text-orange font-medium text-sm tracking-wide"
              onClick={handleEditGuest}
            >
              Add details
            </button>
          </Flex>
          <span className="text-theme-placeholder text-lg mt-1">
            {guestsDetails.length > 0
              ? guestsDetails
                  .map((g) => `${g.name} (${GENDER[g.gender]})`)
                  .join(", ")
              : `${maxGuests} guest(s)`}
          </span>
        </Flex>
      </Section>
      <Section title="Pay with" borderTop>
        <Flex col>
          <span className="text-lg text-theme-placeholder mt-2">
            Credit or Debit Card, Net banking, UPI and Wallet
          </span>
          <span className="text-lg text-theme-placeholder border-t border-theme-surface mt-6 py-6">
            By proceding next, you agree to get great experience with the{" "}
            <button
              className="focus:outline-none underline"
              onClick={handleHouseRules}
            >
              House Rules
            </button>
            ,{" "}
            <button
              className="focus:outline-none underline"
              onClick={handleCancellationPolicy}
            >
              Cancellation Policy
            </button>
            , and{" "}
            <button
              className="focus:outline-none underline"
              onClick={handleTnC}
            >
              Terms and conditions
            </button>
          </span>
        </Flex>
      </Section>
    </section>
  ) : null;
};

export default BookingWidget;
